<script setup>
import { ref, onMounted, nextTick, computed } from "vue";
import { isEmpty, has, get } from "lodash";
import { initSelect2 } from "../vendors/select2.js";

const props = defineProps({
    title: String,
    name: {
        type: String,
        required: true,
    },
    required: Boolean,
    data: {
        type: Array,
        default: () => []
    },
    errors: {
        type: Array,
        default: () => [],
    },
});

const rows = ref(props.data && props.data.length > 0 ? props.data : [{id: 1}]);
const repeaterId = `id-v-repeater-${props.name}`;

// Add computed property to check if we should show no data
const showNoData = computed(() => {
    return isEmpty(props.data) && rows.value.length === 0;
});

onMounted(() => {
    if (isEmpty(props.data)) {
        rows.value.splice(0);
    }

    initializeInputs("all");
});

const addRow = async () => {
    rows.value.push({id: rows.value.length + 1});
    await nextTick();
    initializeInputs("add", rows.value.length - 1);
};

const deleteRow = async (index) => {
    if (!_.isEmpty(rows.value[index].code)) {
        if (!window.confirm('Are you sure you want to delete this row?')) return;
    }

    rows.value.splice(index, 1);
    await nextTick();
    initializeInputs("delete", index);
};

const initializeInputs = (mode = "all", startIndex = 0) => {
    let shouldInitSelect2 = false;

    let rowsToUpdate = [];
    const selector = `#${repeaterId.replace(/\[/g, '\\[').replace(/\]/g, '\\]')} table tbody tr`;
    const tableRows = document.querySelectorAll(selector);

    if (mode === "all") {
        rowsToUpdate = Array.from(tableRows).map((_, i) => i);
    } else if (mode === "add") {
        rowsToUpdate = [startIndex];
    } else if (mode === "delete") {
        rowsToUpdate = Array.from({length: tableRows.length - startIndex}, (_, i) => i + startIndex);
    }

    rowsToUpdate.forEach(index => {
        const row = tableRows[index];
        if (!row) return;

        const inputs = row.querySelectorAll('input, select');
        inputs.forEach((input) => {
            let baseName = getInputBaseName(input);
            let id = `id-${baseName}[${index}]`;
            let name = `${props.name}[${index}][${baseName}]`;
            if (input.nodeName === 'SELECT' && input.hasAttribute('multiple')) {
                name = name + '[]';
            }
            input.setAttribute('id', id);
            input.setAttribute('name', name);

            // set data
            if (!isEmpty(props.data) && !isEmpty(props.data[index]) && has(props.data[index], baseName) && !isEmpty(get(props.data[index], baseName))) {
                $(input).val(get(props.data[index], baseName)).change();
            }

            // errors display
            if (!isEmpty(props.errors)) {
                displayErrorMsg(input);
            }
        });
    });

    rowsToUpdate.forEach(index => {
        const row = tableRows[index];
        if (!row) return;
        const inputs = row.querySelectorAll('input, select');
        inputs.forEach((input) => {
            if (input.hasAttribute('data-toggle') && !shouldInitSelect2) {
                shouldInitSelect2 = true;
            }
        });
    });

    if (shouldInitSelect2) {
        initSelect2();
    }
};

const getInputBaseName = (input) => {
    if (input.getAttribute('data-input-name')) {
        return input.getAttribute('data-input-name');
    }

    const baseName = input.getAttribute('name')
        .replace(/\[(.*?)\]/g, '.$1')
        .replace(/^\./, '')
        .split('.')
        .filter(part => part !== '' && isNaN(part));

    return baseName[baseName.length - 1];
}

const displayErrorMsg = (input) => {
    const errorKey = input.getAttribute('name')
        .replace(/\[(.*?)\]/g, '.$1');

    if (has(props.errors, errorKey)) {
        const errorMsg = get(props.errors, errorKey)[0];

        if (input.nodeName === 'SELECT') {
            $(input).addClass('border-danger');
        } else {
            $(input).parent().addClass('border-danger');
        }

        const errorDiv = document.createElement('div');
        errorDiv.className = 'text-danger';
        errorDiv.textContent = errorMsg;
        input.closest('td').appendChild(errorDiv);
    }
}

// Expose methods to parent if needed
defineExpose({
    addRow,
    deleteRow,
    rows
});
</script>

<template>
    <div class="w-full" :id="repeaterId">
        <div v-if="title" class="flex justify-between items-center mb-2">
            <label class="form-label font-normal text-gray-900 required flex-row items-center">
                <span v-if="title">{{ title }}</span>
                <span v-if="required" class="text-danger">*</span>
            </label>
            <button
                type="button"
                class="btn btn-icon btn-sm btn-light-primary p-2 rounded-full hover:btn-primary"
                @click="addRow"
            >
                <i class="ki-filled ki-plus-squared"></i>
            </button>
        </div>
        <slot name="additional-input"></slot>
        <table class="w-full table table-primary table-auto table-border">
            <thead>
                <tr>
                    <th class="p-2 text-center">#</th>
                    <slot name="th"></slot>
                    <th class="p-2 text-center">Action</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="showNoData">
                    <td colspan="100" class="p-4 text-center text-gray-500">
                        No Data
                    </td>
                </tr>
                <tr v-else v-for="(row, index) in rows" :key="row.id">
                    <td class="p-2 text-center v-repeater-row-index">{{ index + 1 }}</td>
                    <slot name="td" :row="row" :index="index"></slot>
                    <td class="p-2 text-center">
                        <button
                            type="button"
                            class="btn btn-outline btn-danger btn-xs"
                            @click="deleteRow(index)"
                        >
                            <i class="ki-outline ki-trash"></i>
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
